import { installmentScheduleTypeConstants } from "constants/account";
import { validationConstants } from "constants/formValidation";
import { orderTypeConstants } from "constants/order";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import dayjs from "dayjs";
import {
  setAmountInstallmentCount,
  setAmountInstallmentCountBeforeCap,
} from "helpers/paymentPlanInstallments";
import { centsToDollars, dollarsToCents, parsePrice } from "helpers/price";
import {
  IDefaultProcedure,
  IPatientResponsibilityProcedure,
} from "types/Order/Order";
import { SpecialtyList } from "types/Provider/Provider";

import {
  PaymentPlanMode,
  DownPaymentType,
  IValidationResult,
  IPreviewCalculations,
  IPaymentPlanData,
  IInstallmentItem,
} from "./types";

export function groupByCodeDescriptionAndCount(items: any[]): {
  code: number;
  cptDescription: string;
  providers: { name: string; specialty: string; quantity: number }[];
}[] {
  const uniqueWithCount = items.reduce(
    (
      acc: IDefaultProcedure[] | IPatientResponsibilityProcedure[],
      currentItem
    ) => {
      if (
        "procedureId" in currentItem &&
        "providerProcedureId" in currentItem
      ) {
        const { code, procedureId, providerProcedureId } = currentItem;
        const key = `${code}-${procedureId}-${providerProcedureId}`;
        const existingObj = acc.find((obj: any) => Object.keys(obj)[0] === key);
        if (existingObj) {
          (existingObj as any)[key] += 1;
        } else {
          acc.push({ [key]: 1 } as any);
        }
      }
      return acc;
    },
    []
  );

  const uniqueObjectsMap = new Map();
  uniqueWithCount.forEach((obj) => {
    const [key, value] = Object.entries(obj)[0];
    uniqueObjectsMap.set(key, value);
  });

  const grouped = items.reduce(
    (
      acc: {
        code: number;
        cptDescription: string;
        providers: { name: string; specialty: string; quantity: number }[];
      }[],
      currentItem
    ) => {
      const { code, cptDescription } = currentItem;

      let foundItem = acc.find(
        (item) => item.code === code && item.cptDescription === cptDescription
      );

      if (!foundItem) {
        foundItem = { code, cptDescription, providers: [] };
        acc.push(foundItem);
      }

      if (
        "providerName" in currentItem &&
        "specialty" in currentItem &&
        "procedureId" in currentItem &&
        "providerProcedureId" in currentItem
      ) {
        const { providerName, specialty, procedureId, providerProcedureId } =
          currentItem;

        if (
          specialty &&
          providerName &&
          !foundItem.providers.find(
            (currentProvider) => currentProvider.name === providerName
          )
        ) {
          const key = `${code}-${procedureId}-${providerProcedureId}`;
          const quantity: number = uniqueObjectsMap.get(key) || 0;

          foundItem.providers.push({ name: providerName, specialty, quantity });
        }
      }

      return acc;
    },
    []
  );

  return grouped;
}

export interface IServiceProvider {
  key: string;
  label: string;
  isFacility: boolean;
}

export function getUniqueProviders(procedures: any[]): IServiceProvider[] {
  const seen = new Set<string>();
  const others: IServiceProvider[] = [];
  const facilities: IServiceProvider[] = [];

  procedures.forEach((line) => {
    const { providerId, providerName, specialty } = line;
    if (!providerName) return;

    const key =
      providerId !== undefined && providerId !== null
        ? String(providerId)
        : providerName;
    if (seen.has(key)) return;
    seen.add(key);

    const isFacility = specialty === SpecialtyList.FACILITY;
    let label = providerName;
    if (isFacility) {
      label = `${providerName} — Main Campus`;
    } else if (specialty === SpecialtyList.PRIMARY_PROVIDER) {
      label = `${providerName} · Physician`;
    }

    (isFacility ? facilities : others).push({ key, label, isFacility });
  });

  return [...others, ...facilities];
}

export function getAbnReason(isMedicare: boolean) {
  return isMedicare
    ? `Medicare does not consider the procedure(s) listed above medically necessary for your condition or deems them experimental.`
    : `Your insurance does not consider the procedure(s) listed above medically necessary for your condition or deems them experimental.`;
}

// Validation function
export function validatePaymentPlanForm(
  editorMode: PaymentPlanMode,
  numberOfMonths: number | null,
  downPaymentValue: number | null,
  downPaymentType: DownPaymentType,
  estimatedCost: number,
  installmentAmount: number | null = null,
  installmentFrequency: string | null = null
): IValidationResult {
  const isDownPaymentOnly = editorMode === "downpayment";
  const isSetAmount = editorMode === "setAmount";

  // Duration validation: for "months" use input; for setAmount use derived duration (must be 1–50)
  let isDurationValid: boolean;
  if (isDownPaymentOnly) {
    isDurationValid = true;
  } else if (isSetAmount) {
    if (
      installmentAmount == null ||
      installmentAmount <= 0 ||
      downPaymentValue == null
    ) {
      isDurationValid = true; // other validations will catch missing values
    } else {
      let calculatedDownPayment = 0;
      if (downPaymentType === "%" && downPaymentValue) {
        calculatedDownPayment = estimatedCost * (downPaymentValue / 100);
      } else if (downPaymentType === "$") {
        calculatedDownPayment = downPaymentValue;
      }
      const remainingAmount = estimatedCost - calculatedDownPayment;
      // CLE-3081: the API's count (fold rule), before its cap, so an amount too small for 50 payments is rejected.
      const derivedDuration = setAmountInstallmentCountBeforeCap(
        dollarsToCents(remainingAmount),
        dollarsToCents(installmentAmount)
      );
      isDurationValid = derivedDuration >= 1 && derivedDuration <= 50;
    }
  } else {
    isDurationValid =
      numberOfMonths !== null && numberOfMonths >= 1 && numberOfMonths <= 50;
  }

  // Down payment value validation - always required
  const isDownPaymentValid = downPaymentValue !== null && downPaymentValue > 0;

  // Check if it's a whole number for duration
  const isDurationWholeNumber =
    isDownPaymentOnly || isSetAmount
      ? true
      : numberOfMonths === null || Number.isInteger(numberOfMonths);

  // Set amount: require installment amount and frequency
  const isInstallmentAmountValid =
    !isSetAmount || (installmentAmount != null && installmentAmount > 0);
  const isInstallmentFrequencyValid =
    !isSetAmount ||
    (installmentFrequency != null && installmentFrequency !== "");

  // Set amount: installment amount must not exceed remaining amount (max)
  let isInstallmentAmountWithinMax = true;
  if (isSetAmount && downPaymentValue != null && installmentAmount != null) {
    let calculatedDownPayment = 0;
    if (downPaymentType === "%" && downPaymentValue) {
      calculatedDownPayment = estimatedCost * (downPaymentValue / 100);
    } else if (downPaymentType === "$") {
      calculatedDownPayment = downPaymentValue;
    }
    const remainingAmount = estimatedCost - calculatedDownPayment;
    if (installmentAmount > Math.round(remainingAmount * 100) / 100) {
      isInstallmentAmountWithinMax = false;
    }
  }

  // Flat amount validation
  const isFlatAmountValid =
    isDownPaymentOnly ||
    downPaymentType !== "$" ||
    !downPaymentValue ||
    downPaymentValue < estimatedCost;

  let durationError: string | null = null;
  if (!isDurationValid) {
    durationError = validationConstants.PAYMENT_PLAN.DURATION_REQUIRED;
  } else if (!isDurationWholeNumber) {
    durationError = validationConstants.PAYMENT_PLAN.DURATION_WHOLE_NUMBER;
  }

  let downPaymentError: string | null = null;
  if (!isDownPaymentValid) {
    downPaymentError =
      downPaymentType === "%"
        ? validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_PERCENT
        : validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_FLAT;
  } else if (!isFlatAmountValid) {
    downPaymentError = `Amount must be less than ${parsePrice(estimatedCost)}`;
  }

  let installmentAmountError: string | null = null;
  if (isSetAmount && !isInstallmentAmountValid) {
    installmentAmountError =
      validationConstants.PAYMENT_PLAN.INSTALLMENT_AMOUNT_REQUIRED;
  } else if (
    isSetAmount &&
    !isInstallmentAmountWithinMax &&
    downPaymentValue != null
  ) {
    let calculatedDownPayment = 0;
    if (downPaymentType === "%" && downPaymentValue) {
      calculatedDownPayment = estimatedCost * (downPaymentValue / 100);
    } else if (downPaymentType === "$") {
      calculatedDownPayment = downPaymentValue;
    }
    const maxInstallmentAmount = estimatedCost - calculatedDownPayment;
    installmentAmountError = `Installment amount cannot exceed ${parsePrice(
      maxInstallmentAmount
    )}`;
  } else if (isSetAmount && !isDurationValid && downPaymentValue != null) {
    let calculatedDownPayment = 0;
    if (downPaymentType === "%" && downPaymentValue) {
      calculatedDownPayment = estimatedCost * (downPaymentValue / 100);
    } else if (downPaymentType === "$") {
      calculatedDownPayment = downPaymentValue;
    }
    const remainingAmount = estimatedCost - calculatedDownPayment;
    const minInstallmentAmount = Math.ceil((remainingAmount / 50) * 100) / 100;
    installmentAmountError = `Installment amount must be at least ${parsePrice(
      minInstallmentAmount
    )} to keep the payment plan within 50 payments`;
  }
  const installmentFrequencyError =
    isSetAmount && !isInstallmentFrequencyValid
      ? "Please select a payment frequency"
      : null;

  return {
    isDurationValid,
    isDownPaymentValid,
    isDurationWholeNumber,
    isFlatAmountValid,
    isFormValid:
      isDurationValid &&
      isDownPaymentValid &&
      isDurationWholeNumber &&
      isFlatAmountValid &&
      isInstallmentAmountValid &&
      isInstallmentFrequencyValid &&
      isInstallmentAmountWithinMax,
    errors: {
      duration: durationError,
      downPayment: downPaymentError,
      installmentAmount: installmentAmountError,
      installmentFrequency: installmentFrequencyError,
    },
  };
}

// Calculate preview values based on editor inputs
export function calculatePreview(
  estimatedCost: number,
  downPaymentType: DownPaymentType,
  downPaymentValue: number | null,
  numberOfMonths: number | null,
  editorMode: PaymentPlanMode,
  installmentAmount: number | null = null
): IPreviewCalculations {
  let calculatedDownPayment = 0;

  if (downPaymentType === "%" && downPaymentValue) {
    calculatedDownPayment = estimatedCost * (downPaymentValue / 100);
  } else if (downPaymentType === "$" && downPaymentValue) {
    calculatedDownPayment = downPaymentValue;
  }

  const isDownPaymentOnly = editorMode === "downpayment";
  const isSetAmount = editorMode === "setAmount";
  const remainingAmount = estimatedCost - calculatedDownPayment;

  let monthlyPayment = 0;
  let months = numberOfMonths || 0;

  if (isSetAmount && installmentAmount != null && installmentAmount > 0) {
    monthlyPayment = installmentAmount;
    // CLE-3081: the API's count (fold rule and 50 cap).
    months = Math.max(
      1,
      setAmountInstallmentCount(
        dollarsToCents(remainingAmount),
        dollarsToCents(installmentAmount)
      )
    );
  } else if (!isDownPaymentOnly && months > 0) {
    monthlyPayment = Math.floor((remainingAmount / months) * 100) / 100;
  }

  return {
    downPayment: calculatedDownPayment,
    monthlyPayment,
    remainingAmount,
    totalPayments: isDownPaymentOnly ? 0 : months,
    isDownPaymentOnly,
  };
}

// Build payment plan data for API submission
export function buildPaymentPlanData(
  editorMode: PaymentPlanMode,
  numberOfMonths: number | null,
  downPaymentType: DownPaymentType,
  downPaymentValue: number | null,
  providerDistributionLogic?: distributionLogicValueList,
  installmentAmount: number | null = null,
  installmentFrequency: "Monthly" | "Weekly" | "Biweekly" | null = null,
  setAmountDuration: number | null = null
): IPaymentPlanData {
  const isDownPaymentOnly = editorMode === "downpayment";
  const isSetAmount = editorMode === "setAmount";

  let paymentPlanDuration: number;
  if (isSetAmount) {
    paymentPlanDuration = setAmountDuration ?? 1;
  } else if (isDownPaymentOnly) {
    paymentPlanDuration = 1;
  } else {
    paymentPlanDuration = numberOfMonths || 1;
  }

  const base: IPaymentPlanData = {
    remainingAmountInCents: null,
    paymentPlanDuration,
    paymentPlanMinDownPaymentPercent:
      downPaymentType === "%" && downPaymentValue ? downPaymentValue : null,
    paymentPlanMinDownPaymentFlat:
      downPaymentType === "$" && downPaymentValue
        ? dollarsToCents(downPaymentValue)
        : null,
    active: true,
    hasInstallments: false,
    isDownPaymentOnly,
    providerDistributionLogic:
      providerDistributionLogic || distributionLogicValueList.BY_PRIORITY,
    differenceByProvider: {},
    valuesByProvider: {},
    calculationBasis: isSetAmount ? "InstallmentAmount" : "Duration",
    installmentFrequency: isSetAmount
      ? installmentFrequency ?? "Monthly"
      : "Monthly",
    installmentAmountInCents:
      isSetAmount && installmentAmount != null && installmentAmount > 0
        ? dollarsToCents(installmentAmount)
        : null,
  };

  return base;
}

// Get estimated cost based on order type
export function getEstimatedCost(
  orderType: string,
  clearPrice: number,
  fullPrice: number
): number {
  const orderTypeAmountMap = new Map<string, number>([
    [orderTypeConstants.BARIATRICS, clearPrice],
    [orderTypeConstants.BUNDLED, clearPrice],
    [orderTypeConstants.PATIENT_RESPONSIBILITY, fullPrice],
    [orderTypeConstants.GFE, fullPrice],
    [orderTypeConstants.ED, clearPrice],
  ]);

  return orderTypeAmountMap.get(orderType) || 0;
}

/**
 * The deposit due up front on a payment plan, in CENTS — the unit the checkout screens charge in.
 * (`calculateInitialPayment` below is the same quantity in dollars, for the plan-builder preview.)
 *
 * CLE-2791: the two checkout screens each rolled their own version of this. Stripe sized the
 * deposit off `getEstimatedCost`, which for GFE and Patient Responsibility is the UNDISCOUNTED
 * total — the negotiated discount is a pay-in-full incentive that a payment plan does not get —
 * while the Finix branch sized it off the discounted Clear Price, so the same plan asked a GFE
 * patient for a visibly smaller deposit on Finix than on Stripe. The backend already sizes the
 * remaining balance off the same undiscounted basis (`calculatePaymentPlanRemainingAmountInCents`
 * in `utils/money.ts`, CLE-2204), so the Finix deposit disagreed with its own schedule.
 *
 * A flat deposit is already in cents. A percentage one multiplies dollars by a WHOLE percent, which
 * lands in cents by construction ($150 × 90 = 13500¢ = $135); it can still be fractional, so round
 * — Finix rejects sub-cent amounts.
 */
export function getInitialPaymentInCents({
  orderType,
  clearPrice,
  fullPrice,
  paymentPlanMinDownPaymentPercent,
  paymentPlanMinDownPaymentFlat,
}: {
  orderType: string;
  clearPrice: number;
  fullPrice: number;
  paymentPlanMinDownPaymentPercent?: number | null;
  paymentPlanMinDownPaymentFlat?: number | null;
}): number {
  const estimatedCost = getEstimatedCost(orderType, clearPrice, fullPrice);
  return Math.round(
    paymentPlanMinDownPaymentFlat ||
      estimatedCost * (paymentPlanMinDownPaymentPercent || 0)
  );
}

// Calculate initial payment from active payment plan
export function calculateInitialPayment(
  activePaymentPlan: any,
  estimatedCost: number
): number {
  if (!activePaymentPlan) return 0;

  const percentage = activePaymentPlan.paymentPlanMinDownPaymentPercent;
  const flatAmount = activePaymentPlan.paymentPlanMinDownPaymentFlat;

  if (flatAmount) {
    return centsToDollars(flatAmount);
  }

  return estimatedCost * (percentage / 100);
}

// Calculate monthly payment
export function calculateMonthlyPayment(
  estimatedCost: number,
  initialPayment: number,
  paymentPlanDuration: number
): number {
  if (!paymentPlanDuration) return 0;

  const remainingAmount = estimatedCost - initialPayment;
  return Math.floor((remainingAmount / paymentPlanDuration) * 100) / 100;
}

export type InstallmentFrequency = "Monthly" | "Weekly" | "Biweekly";
export function addMonthsLikeBackend(date: Date, monthsToAdd: number) {
  const d = new Date(date.getTime());
  // Mimic backend behavior: native Date month overflow (Jan 30 + 1 month => Mar 2 in 2026)
  d.setMonth(d.getMonth() + monthsToAdd);
  return d;
}

export function addWeeksLikeBackend(date: Date, weeksToAdd: number) {
  const d = new Date(date.getTime());
  // Mimic backend behavior: native Date day overflow
  d.setDate(d.getDate() + weeksToAdd * 7);
  return d;
}

/** Match backend: no installment due dates on weekends (Sat → Mon, Sun → Mon). */
export function adjustInstallmentDateForWeekend(date: Date): Date {
  const d = new Date(date.getTime());
  const day = d.getUTCDay();
  if (day === 6) d.setUTCDate(d.getUTCDate() + 2);
  else if (day === 0) d.setUTCDate(d.getUTCDate() + 1);
  return d;
}

// Generate installments list for display
export function generateInstallments(
  orderInfo: any,
  initialPayment: number,
  estimatedCost: number,
  paymentPlanDuration: number,
  options?: {
    installmentAmount?: number;
    installmentFrequency?: InstallmentFrequency;
  }
): IInstallmentItem[] {
  if (!paymentPlanDuration) return [];

  const remainingAmount = estimatedCost - initialPayment;
  const isSetAmount =
    options?.installmentAmount != null &&
    options.installmentAmount > 0 &&
    options?.installmentFrequency != null;

  let basePayment: number;
  let lastPayment: number;
  if (isSetAmount && options?.installmentAmount != null) {
    basePayment = options.installmentAmount;
    lastPayment = remainingAmount - basePayment * (paymentPlanDuration - 1);
  } else {
    basePayment =
      Math.floor((remainingAmount / paymentPlanDuration) * 100) / 100;
    lastPayment = remainingAmount - basePayment * (paymentPlanDuration - 1);
  }

  const { installmentScheduleType } = orderInfo.account;
  const isTwoWeekSchedule =
    installmentScheduleType ===
    installmentScheduleTypeConstants.TWO_WEEKS_BEFORE;

  let startDate: dayjs.Dayjs;
  let addAMonth = true;

  if (isTwoWeekSchedule) {
    const twoWeeksBefore = dayjs
      .utc(orderInfo.dateOfService)
      .subtract(14, "days");
    const now = dayjs.utc();

    startDate = twoWeeksBefore.isBefore(now) ? dayjs() : twoWeeksBefore;
    addAMonth = !!twoWeeksBefore.isBefore(now);
  } else {
    startDate = dayjs();
  }
  // Convert startDate to a native Date once, then do backend-style setMonth math
  const startNativeDate = startDate.toDate();

  const frequency = options?.installmentFrequency ?? "Monthly";

  const installments = Array.from(
    { length: paymentPlanDuration },
    (_, index) => {
      let date: string;
      if (frequency === "Weekly") {
        const weeksFromStart = addAMonth ? index + 1 : index;
        const dueNative = adjustInstallmentDateForWeekend(
          addWeeksLikeBackend(startNativeDate, weeksFromStart)
        );
        date = dayjs(dueNative).format("YYYY-MM-DD");
      } else if (frequency === "Biweekly") {
        const twoWeekPeriodsFromStart = addAMonth ? index + 1 : index;
        const dueNative = adjustInstallmentDateForWeekend(
          addWeeksLikeBackend(startNativeDate, twoWeekPeriodsFromStart * 2)
        );
        date = dayjs(dueNative).format("YYYY-MM-DD");
      } else {
        const updatedIndex = addAMonth ? index + 1 : index;
        const dueNative = adjustInstallmentDateForWeekend(
          addMonthsLikeBackend(startNativeDate, updatedIndex)
        );
        date = dayjs(dueNative).format("YYYY-MM-DD");
      }
      const payment =
        index === paymentPlanDuration - 1 ? lastPayment : basePayment;

      return { date, payment };
    }
  );

  const initialPaymentObj: IInstallmentItem = {
    date: "Due Today",
    payment: initialPayment,
  };

  return [initialPaymentObj, ...installments];
}

// Initialize editor state from existing payment plan
export interface IEditorInitState {
  numberOfMonths: number | null;
  downPaymentType: DownPaymentType;
  downPaymentValue: number | null;
  installmentAmount: number | null;
  installmentFrequency: "Monthly" | "Weekly" | "Biweekly" | null;
}

export function initializeEditorState(
  activePaymentPlan: any
): IEditorInitState {
  if (activePaymentPlan) {
    const hasFlat = !!activePaymentPlan.paymentPlanMinDownPaymentFlat;
    const isSetAmount =
      activePaymentPlan.calculationBasis === "InstallmentAmount";

    return {
      numberOfMonths: isSetAmount
        ? activePaymentPlan.paymentPlanDuration
        : activePaymentPlan.paymentPlanDuration || null,
      downPaymentType: hasFlat ? "$" : "%",
      downPaymentValue: hasFlat
        ? centsToDollars(activePaymentPlan.paymentPlanMinDownPaymentFlat)
        : activePaymentPlan.paymentPlanMinDownPaymentPercent || null,
      installmentAmount: isSetAmount
        ? centsToDollars(activePaymentPlan.installmentAmountInCents || 0)
        : null,
      installmentFrequency: isSetAmount
        ? activePaymentPlan.installmentFrequency || "Monthly"
        : null,
    };
  }

  return {
    numberOfMonths: null,
    downPaymentType: "$",
    downPaymentValue: null,
    installmentAmount: null,
    installmentFrequency: null,
  };
}
